import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  addDays, iso, startOfMonth, endOfMonth, startOfWeek, rangeDays,
  MOIS, JOURS_LONG,
} from "@/lib/date";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";

// Vue calendrier (mois complet 5-6 semaines) — lecture seule.
// Clic sur un jour → onSelectDay(date) pour naviguer vers la vue semaine.
export default function PlanningMonth({ monthStart, setMonthStart, onSelectDay, showAvails = false }) {
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [holidays, setHolidays] = useState(new Set());
  const [avails, setAvails] = useState([]);

  const monthEnd = useMemo(() => endOfMonth(monthStart), [monthStart]);
  const gridStart = useMemo(() => startOfWeek(startOfMonth(monthStart)), [monthStart]);
  // Toujours 6 semaines pour une hauteur stable
  const gridEnd = useMemo(() => addDays(gridStart, 41), [gridStart]);
  const days = useMemo(() => rangeDays(gridStart, gridEnd), [gridStart, gridEnd]);

  const load = async () => {
    try {
      const [sh, us, hs, av] = await Promise.all([
        api.get("/shifts", { params: { start: iso(gridStart), end: iso(gridEnd) } }),
        api.get("/users"),
        api.get(`/holidays/${monthStart.getFullYear()}`),
        api.get("/availabilities", { params: { start: iso(gridStart), end: iso(gridEnd) } }),
      ]);
      setShifts(sh.data);
      setUsers(us.data);
      setHolidays(new Set(hs.data.map((h) => h.date)));
      setAvails(av.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthStart]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);

  const firstName = (u) => u?.first_name || (u?.name || "").split(" ")[0] || "";

  // Compteurs de dispos par date : {date: [{userId, first_name, start, end}]}
  const availsByDate = useMemo(() => {
    const map = {};
    avails.forEach((a) => {
      if (!userMap[a.user_id]) return;
      if (!a.available) return;
      const u = userMap[a.user_id];
      (map[a.date] = map[a.date] || []).push({
        userId: a.user_id,
        firstName: firstName(u) || u.name,
        start: (a.start || "").slice(0, 5),
        end: (a.end || "").slice(0, 5),
      });
    });
    // tri par prénom
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.firstName.localeCompare(b.firstName)));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avails, userMap]);

  // Grouper shifts par date, triés par start
  const shiftsByDate = useMemo(() => {
    const m = {};
    shifts.forEach((s) => {
      if (s.cancelled) return;
      if (!s.student_id || !userMap[s.student_id]) return; // skip orphelins
      (m[s.date] = m[s.date] || []).push(s);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.start || "").localeCompare(b.start || "")));
    return m;
  }, [shifts, userMap]);

  // Total heures par étudiant pour le MOIS
  const monthHoursByStudent = useMemo(() => {
    const map = {};
    const seen = new Set();
    shifts.forEach((s) => {
      if (!s || s.cancelled) return;
      if (s.id && seen.has(s.id)) return;
      if (s.id) seen.add(s.id);
      if (!s.student_id || !userMap[s.student_id]) return; // skip orphelins
      if (!s.date || s.date < iso(startOfMonth(monthStart)) || s.date > iso(monthEnd)) return;
      const [h1, m1] = (s.start || "0:0").split(":").map(Number);
      const [h2, m2] = (s.end || "0:0").split(":").map(Number);
      const mins = Math.max(0, ((h2 || 0) * 60 + (m2 || 0)) - ((h1 || 0) * 60 + (m1 || 0)));
      map[s.student_id] = (map[s.student_id] || 0) + mins;
    });
    return map;
  }, [shifts, monthStart, monthEnd, userMap]);

  const fmtHours = (mins) => {
    if (!mins) return "0h";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  };

  const prevMonth = () => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1));
  const nextMonth = () => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1));

  const exportMonthCSV = () => {
    // 1 ligne par shift : Date;Étudiant;Début;Fin;Publié
    const rows = [["Date", "Jour", "Étudiant", "Début", "Fin", "Statut"].join(";")];
    Object.entries(shiftsByDate)
      .filter(([d]) => d >= iso(startOfMonth(monthStart)) && d <= iso(monthEnd))
      .sort()
      .forEach(([d, arr]) => {
        const dObj = new Date(d);
        const jourLong = JOURS_LONG[(dObj.getDay() + 6) % 7];
        arr.forEach((s) => {
          const u = userMap[s.student_id];
          rows.push([d, jourLong, u?.name || s.student_id, s.start || "", s.end || "",
            s.published ? "Publié" : "Brouillon"].join(";"));
        });
      });
    const csv = "\uFEFF" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `planning-${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, "0")}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("Mois exporté");
  };

  const isInMonth = (d) => d.getMonth() === monthStart.getMonth();
  const monthLabel = `${MOIS[monthStart.getMonth()]} ${monthStart.getFullYear()}`;

  return (
    <div className="space-y-5" data-testid="planning-month">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="month-prev" className="rounded-full hover:bg-[#F0F5F3]">
            <ChevronLeft className="w-5 h-5 text-[#2D5951]" />
          </Button>
          <h2 className="font-display text-2xl text-[#1A2B27] min-w-[210px] text-center capitalize">{monthLabel}</h2>
          <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="month-next" className="rounded-full hover:bg-[#F0F5F3]">
            <ChevronRight className="w-5 h-5 text-[#2D5951]" />
          </Button>
        </div>
        <Button
          onClick={exportMonthCSV}
          variant="outline"
          data-testid="export-month-button"
          className="rounded-full border-[#2D5951] text-[#2D5951] hover:bg-[#E8F0EE]"
        >
          <Download className="w-4 h-4 mr-2" /> Exporter le mois
        </Button>
      </div>

      <Card className="card-soft border-0 overflow-hidden">
        <CardContent className="p-0">
          <div className="grid grid-cols-7 border-b border-[#DDE7E4] bg-[#F0F5F3]">
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((j) => (
              <div key={j} className="p-2.5 text-[11px] font-semibold uppercase tracking-wider text-[#2D5951] text-center">
                {j}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const isoD = iso(d);
              const inMonth = isInMonth(d);
              const isHoliday = holidays.has(isoD);
              const isSunday = d.getDay() === 0;
              const dayShifts = shiftsByDate[isoD] || [];
              const closed = isHoliday || isSunday;
              return (
                <button
                  key={isoD}
                  type="button"
                  onClick={() => onSelectDay(d)}
                  data-testid={`month-day-${isoD}`}
                  title={`Ouvrir la semaine de ${isoD}`}
                  className={`text-left border-b border-r border-[#DDE7E4] p-2 min-h-[110px] hover:bg-[#F5FAF8] transition-colors relative ${
                    inMonth ? "bg-white" : "bg-[#FAFCFB] opacity-60"
                  } ${closed ? "bg-[#F0F5F3]/60" : ""}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold ${inMonth ? "text-[#1A2B27]" : "text-[#94ADA7]"}`}>
                      {d.getDate()}
                    </span>
                    {closed && (
                      <span className="text-[9px] uppercase text-[#94ADA7] tracking-wide">
                        {isSunday ? "Dim." : "Férié"}
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayShifts.slice(0, 4).map((s) => {
                      const u = userMap[s.student_id];
                      const bg = s.published ? "bg-[#E8F0EE] text-[#1F423C]" : "bg-[#FFF3E0] text-[#8B5A00] border border-[#FFD54F]/40";
                      return (
                        <div
                          key={s.id}
                          data-testid={`month-shift-${s.id}`}
                          className={`text-[10px] leading-tight rounded px-1.5 py-0.5 truncate ${bg}`}
                        >
                          <span className="font-semibold">{firstName(u) || "?"}</span>
                          <span className="ml-1 opacity-80">{s.start}–{s.end}</span>
                        </div>
                      );
                    })}
                    {dayShifts.length > 4 && (
                      <div className="text-[10px] text-[#6B7F79] font-medium">
                        +{dayShifts.length - 4} autre{dayShifts.length - 4 > 1 ? "s" : ""}…
                      </div>
                    )}
                    {/* Dispos non planifiées (que si toggle actif, cellule ouverte, et étudiant sans shift ce jour-là) */}
                    {showAvails && !closed && inMonth && (() => {
                      const scheduledIds = new Set(dayShifts.map((s) => s.student_id));
                      const availList = (availsByDate[isoD] || []).filter((a) => !scheduledIds.has(a.userId));
                      const maxRows = Math.max(0, 5 - Math.min(dayShifts.length, 4) - (dayShifts.length > 4 ? 1 : 0));
                      const shown = availList.slice(0, maxRows);
                      const hidden = availList.length - shown.length;
                      return (
                        <>
                          {shown.map((a) => (
                            <div
                              key={`av-${a.userId}`}
                              data-testid={`month-avail-${isoD}-${a.userId}`}
                              title={`Dispo : ${a.firstName}${a.start ? ` — ${a.start}–${a.end}` : ""}`}
                              className="text-[10px] leading-tight rounded px-1.5 py-0.5 truncate bg-[#F1F8E9] text-[#1B5E20] border border-dashed border-[#4CAF50]/60"
                            >
                              <span className="font-semibold">{a.firstName}</span>
                              {a.start && <span className="ml-1 opacity-80">{a.start}–{a.end}</span>}
                            </div>
                          ))}
                          {hidden > 0 && (
                            <div className="text-[10px] text-[#4CAF50] font-medium italic">
                              +{hidden} dispo{hidden > 1 ? "s" : ""}…
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="card-soft border-0">
        <CardContent className="p-5">
          <h3 className="font-display text-lg text-[#1A2B27] mb-3">Totaux du mois</h3>
          {Object.keys(monthHoursByStudent).length === 0 ? (
            <p className="text-sm text-[#6B7F79]">Aucun shift ce mois-ci.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {Object.entries(monthHoursByStudent)
                  .sort((a, b) => b[1] - a[1])
                  .map(([sid, mins]) => {
                    const u = userMap[sid];
                    return (
                      <div
                        key={sid}
                        data-testid={`month-total-${sid}`}
                        className="inline-flex items-center gap-2 bg-[#E8F0EE] rounded-full pl-3 pr-2 py-1 border border-[#C9DBD6]"
                      >
                        <span className="text-sm font-medium text-[#1A2B27]">{u?.name || sid}</span>
                        <span className="text-xs font-bold text-[#2D5951] bg-white rounded-full px-2 py-0.5">
                          {fmtHours(mins)}
                        </span>
                      </div>
                    );
                  })}
              </div>
              <div className="mt-4 pt-4 border-t border-[#DDE7E4] flex items-center justify-between" data-testid="month-grand-total">
                <span className="text-sm font-semibold text-[#1A2B27]">Total équipe — {monthLabel}</span>
                <span className="text-2xl font-display text-[#2D5951]" data-testid="month-grand-total-value">
                  {fmtHours(Object.values(monthHoursByStudent).reduce((a, b) => a + b, 0))}
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B7F79]">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#E8F0EE] border border-[#C9DBD6]"></span> Publié</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#FFF3E0] border border-[#FFD54F]/60"></span> Brouillon</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#F1F8E9] border border-dashed border-[#4CAF50]/60"></span> Dispo (non planifiée)</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#F0F5F3]"></span> Fermé (dim. / férié)</span>
        <span className="ml-auto italic">Clique un jour pour éditer la semaine</span>
      </div>
    </div>
  );
}
